// React
import React, { useState, useContext, useRef, useEffect } from 'react';

// Date Utils
import 'date-fns';
import DateFnsUtils from '@date-io/date-fns';

// Styles
import styles from './ExportFilter.module.scss';

// Third party
import PropTypes from 'prop-types';
import nextId from 'react-id-generator';
import { trackPromise } from 'react-promise-tracker';

// Material UI
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText
} from '@material-ui/core';

import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker
} from '@material-ui/pickers';

// Contexts
import { ListingContext } from '../../../contexts/listing';

// Services
import { getListingData } from '../../../services/taxonomies';

// Hooks
import useMounted from '../../../hooks/useMounted';

// This component
export default function ExportFilter({
  type,
  label,
  parameter,
  staticData,
  endpoint,
  getRows,
  disabled,
  setDisabled,
  isSelectLabelAsValue = false,
  dateFormat = "YYYY-MM-DD",
  isPlaceholderRendered = true
}) {
  // Refeference (input)
  const input = useRef(null);

   // Listing Context
  const { activeExportFilters, saveActiveExportFilters } = useContext(ListingContext);

  // Helper function to get and prepare the value
  const getValue = () => {
    let result = activeExportFilters && activeExportFilters.hasOwnProperty(parameter) ? activeExportFilters[parameter] : '';
    if (type === 'multiselect') {
      result = result.split('|'); // the values will be split by '|' character
    }
    return result;
  };

  // Field value
  const [value, setValue] = useState(getValue);
  const [selectedDate, setSelectedDate] = useState(null);

  // Disable next field (used only on "duo" type)
  const [disableNextField, setDisableNextField] = useState(true);

  // If the component is mounted
  const mounted = useMounted();

  // Data
  const [data, setData] = useState(
    staticData || {
      data: []
    }
  );

  // Keep reference to the inital passed function (we don't need to watch its changes, just need to invoke)
  const getRowsRef = useRef(getRows);

  // Handle change
  const handleChange = (event) => {
    const newValue = event.target.value;
    const name = event.target.name;

    let copy = JSON.parse(JSON.stringify(activeExportFilters));

    if (type === 'multiselect') {
      const indexOfEmptyVal = newValue.indexOf('');
      if (indexOfEmptyVal > -1) {
        newValue.splice(indexOfEmptyVal, 1);
      }

      if (newValue.length === 0) {
        delete copy[name];
      } else {
        copy[name] = newValue.join('|'); // activeFilters is passed as string like a|b|c
      }
    } else {
      if (newValue === '') {
        delete copy[name];
      } else {
        copy[name] = newValue;
      }
    }

    setValue(newValue);
    saveActiveExportFilters(copy);
  };

  // Handle date input change
  const handleDateChange = (date, value) => {
    const newValue = value;
    const name = parameter;

    let copy = JSON.parse(JSON.stringify(activeExportFilters));

    if (newValue === '' || newValue === null) {
      delete copy[name];
    } else {
      copy[name] = newValue;
    }

    setSelectedDate(date);
    saveActiveExportFilters(copy);
  };

  // Component did mount (get data)
  useEffect(() => {
    // Dynamic data
    if ((type === 'select' || type === 'multiselect') && endpoint) {
      const promise = getListingData({
        endpoint,
        getRows: getRowsRef.current
      });

      trackPromise(
        promise.then((newData) => {
          if (
            mounted.current &&
            newData &&
            newData.data &&
            newData.data.length
          ) {
            setData(newData);
          }
        })
      );
    }
  }, [type, endpoint, mounted]);

  // Component did mount ("duo" type)
  useEffect(() => {
    if (typeof setDisabled == 'function') {
      setDisabled(value === '');
    }
  }, [value, setDisabled]);

  // Select
  if (type === 'select') {
    const uniqueId = nextId('tps-unique-id-');

    return (
      <FormControl variant="outlined" fullWidth={true}>
        <InputLabel id={uniqueId}>{label}</InputLabel>
        <Select
          inputRef={input}
          classes={{ select: styles.select }}
          labelId={uniqueId}
          name={parameter}
          label={label}
          value={value === undefined || value === null || !data.data || data.data.length === 0 ? [] : value}
          onChange={handleChange}>
          {
            isPlaceholderRendered &&
            <MenuItem value="">
              <em>Select</em>
            </MenuItem>
          }
          {data.data &&
            data.data.map((item, i) => {
              return (
                <MenuItem key={i} value={isSelectLabelAsValue ? item.label : item.value}>
                  {item.label}
                </MenuItem>
              );
            })}
        </Select>
      </FormControl>
    );
  }

  // Milti select
  else if (type === 'multiselect') {
    const uniqueId = nextId('tps-unique-id-');

    return (
      <FormControl variant="outlined" fullWidth={true}>
        <InputLabel id={uniqueId}>{label}</InputLabel>
        <Select
          multiple={true}
          inputRef={input}
          classes={{ select: styles.select }}
          labelId={uniqueId}
          name={parameter}
          label={label}
          value={value === undefined || value === null || !data.data || data.data.length === 0 ? [] : value}
          onChange={handleChange}
          renderValue={(selected) => selected.join(', ')}>
          <MenuItem value="">
            <em>Select</em>
          </MenuItem>
          {data.data &&
            data.data.map((item, i) => {
              return (
                <MenuItem key={i} value={isSelectLabelAsValue ? item.label : item.value}>
                  <Checkbox checked={value.indexOf(item.value) > -1}/>
                  <ListItemText primary={item.label}/>
                </MenuItem>
              );
            })}
        </Select>
      </FormControl>
    );
  }

  // Duo field
  else if (type === 'duo') {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={5}>
          <ExportFilter
            type="select"
            parameter={parameter[0]}
            label={label}
            staticData={{
              data: [
                {
                  label: 'Contains',
                  value: 'C'
                },
                {
                  label: 'Starts With',
                  value: 'S'
                },
                {
                  label: 'Ends With',
                  value: 'F'
                },
                {
                  label: 'Equals',
                  value: 'E'
                }
              ]
            }}
            setDisabled={setDisableNextField}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={7}>
          <ExportFilter
            type="text"
            parameter={parameter[1]}
            label={label}
            disabled={disableNextField}
          />
        </Grid>
      </Grid>
    );
  }

  // Datepicker field
  else if (type === 'datepicker') {
    return (
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <KeyboardDatePicker
          clearable={true}
          disableToolbar
          inputVariant="outlined"
          className={styles['input-text']}
          disabled={disabled === true}
          format="yyyy-MM-dd"
          fullWidth={true}
          label={label}
          name={parameter}
          value={selectedDate}
          onChange={handleDateChange}
          KeyboardButtonProps={{
            'aria-label': `Change ${label}`
          }}
        />
      </MuiPickersUtilsProvider>
    );
  }

  // Text Field (deafult)
  return (
    <TextField
      inputRef={input}
      className={styles['input-text']}
      variant="outlined"
      name={parameter}
      label={label}
      fullWidth={true}
      onBlur={handleChange}
      disabled={disabled === true}
    />
  );
}

// Properties
ExportFilter.propTypes = {
  type: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  parameter: PropTypes.any.isRequired
};
